<template>
    <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
        <VaAccordion v-model="value" multiple>
            <template v-for="route in navigationRoutes.routes" :key="route.name">
                <VaCollapse v-if="shouldShowRoute(route) && shouldShowChildren(route)">
                    <template #header="{ value: isCollapsed }">
                        <VaSidebarItem
                            :to="getRouteTo(route)"
                            :active="routeHasActiveChild(route)"
                            :active-color="activeColor"
                            :text-color="textColor(route)"
                            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
                            role="button"
                            hover-opacity="0.10"
                        >
                            <VaSidebarItemContent class="py-3 pr-2 pl-4">
                                <VaIcon
                                    v-if="route.meta.icon"
                                    aria-hidden="true"
                                    :name="route.meta.icon"
                                    size="20px"
                                    :color="iconColor(route)"
                                />
                                <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                                    {{ t(route.displayName) }}
                                    <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                                </VaSidebarItemTitle>
                            </VaSidebarItemContent>
                        </VaSidebarItem>
                    </template>
                    <template #body>
                        <div v-for="childRoute in route.children" :key="childRoute.name">
                            <VaSidebarItem
                                :to="{ name: childRoute.name }"
                                :active="isActiveChildRoute(childRoute)"
                                :active-color="activeColor"
                                :text-color="textColor(childRoute)"
                                :aria-label="`Visit ${t(route.displayName)}`"
                                hover-opacity="0.10"
                            >
                                <VaSidebarItemContent class="py-3 pr-2 pl-11">
                                    <VaSidebarItemTitle class="leading-5 font-semibold">
                                        {{ t(childRoute.displayName) }}
                                    </VaSidebarItemTitle>
                                </VaSidebarItemContent>
                            </VaSidebarItem>
                        </div>
                    </template>
                </VaCollapse>

                <VaSidebarItem
                    v-else-if="shouldShowRoute(route)"
                    :to="getRouteTo(route)"
                    :active="routeHasActiveChild(route)"
                    :active-color="activeColor"
                    :text-color="textColor(route)"
                    :aria-label="`Visit ${t(route.displayName)}`"
                    hover-opacity="0.10"
                >
                    <VaSidebarItemContent class="py-3 pr-2 pl-4">
                        <VaIcon
                            v-if="route.meta.icon"
                            aria-hidden="true"
                            :name="route.meta.icon"
                            size="20px"
                            :color="iconColor(route)"
                        />
                        <VaSidebarItemTitle class="leading-5 font-semibold">
                            {{ t(route.displayName) }}
                        </VaSidebarItemTitle>
                    </VaSidebarItemContent>
                </VaSidebarItem>
            </template>
        </VaAccordion>
    </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { storeToRefs } from 'pinia'

import { OpsUserRole } from '../../pages/ops-users/types'
import { useUserStore } from '../../stores/user-store'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
    name: 'Sidebar',
    props: {
        visible: { type: Boolean, default: true },
        mobile: { type: Boolean, default: false },
    },
    emits: ['update:visible'],

    setup: (props, { emit }) => {
        const { getColor, colorToRgba } = useColors()
        const route = useRoute()
        const { t } = useI18n()
        const userStore = useUserStore()
        const { userRole } = storeToRefs(userStore)

        const value = ref<boolean[]>([])
        const isAdmin = computed(() => userRole.value === OpsUserRole.Admin)
        const isPromoter = computed(() => userRole.value === OpsUserRole.Promoter)
        const isOperator = computed(() => userRole.value === OpsUserRole.Operator)

        const allowedRouteNames = computed<Set<string> | null>(() => {
            if (isAdmin.value) {
                return null
            }

            if (isPromoter.value) {
                return new Set(['affiliate-me', 'auth'])
            }

            if (isOperator.value) {
                return new Set(['auth'])
            }

            return new Set(['auth'])
        })

        const writableVisible = computed({
            get: () => props.visible,
            set: (v: boolean) => emit('update:visible', v),
        })

        const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

        const routeHasActiveChild = (section: INavigationRoute) => {
            if (!section.children) {
                return route.name === section.name || route.path.endsWith(`${section.name}`)
            }

            return section.children.some(({ name }) => route.name === name || route.path.endsWith(`${name}`))
        }

        const shouldShowRoute = (section: INavigationRoute) => {
            if (!allowedRouteNames.value) {
                return true
            }

            return allowedRouteNames.value.has(section.name)
        }

        const shouldShowChildren = (section: INavigationRoute) => {
            if (!section.children?.length) {
                return false
            }

            if (isAdmin.value) {
                return true
            }

            return isPromoter.value && section.name === 'affiliate-me'
        }

        const getRouteTo = (section: INavigationRoute) => {
            if (shouldShowChildren(section)) {
                return undefined
            }

            if (section.children?.length) {
                return { name: section.children[0].name }
            }

            return { name: section.name }
        }

        const setActiveExpand = () =>
            (value.value = navigationRoutes.routes
                .filter(shouldShowRoute)
                .map((route: INavigationRoute) => routeHasActiveChild(route)))

        const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
        const color = computed(() => getColor('background-secondary'))
        const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

        const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
        const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
        const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

        watch(() => route.fullPath, setActiveExpand, { immediate: true })

        return {
            writableVisible,
            sidebarWidth,
            value,
            color,
            activeColor,
            navigationRoutes,
            isAdmin,
            isPromoter,
            isOperator,
            shouldShowRoute,
            shouldShowChildren,
            getRouteTo,
            routeHasActiveChild,
            isActiveChildRoute,
            t,
            iconColor,
            textColor,
            arrowDirection,
        }
    },
})
</script>
